import React from "react";
import { useParams, Navigate } from "react-router-dom";

import classNames from "classnames";

import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TablePaginationConfig } from "antd/lib/table";
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import { MinusCircleFilled } from "@ant-design/icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleLeft,
  faArrowAltCircleRight,
} from "@fortawesome/free-solid-svg-icons";
import { Arrow } from "../Icons";

import { BitmakerAPIClient } from "../../services/BitmakerApp";

import { UserAvailabilityTrafficLight } from "./UserAvailability";

import {
  SprintIssue,
  SprintIssueFromJSON,
} from "../../bitmaker/schemas/Sprints/SprintIssue";
import { Sprint, SprintFromJSON } from "../../bitmaker/schemas/Sprint";
import {
  SprintUser,
  SprintUserFromJSON,
} from "../../bitmaker/schemas/Sprints/SprintUser";
import {
  SprintTimeOffUser,
  SprintTimeOffUserFromJSON,
} from "../../bitmaker/schemas/Sprints/SprintTimeOffUser";

import { UserBasicFromJSON } from "../../bitmaker/schemas/Users/UserBasic";
import {
  generateFilters,
  generateSprintDates,
  hoursReadable,
  fromDateToStringWithFullFormat,
  toUTCDateOnly,
} from "../../utils";
import { REDMINE_URL } from "../../constants";

const SprintPage = () => {
  const { id } = useParams<{ id: string }>();

  const sprintId = id ? parseInt(id, 10) : undefined;

  if (id && isNaN(sprintId!)) {
    return <div>Error: Invalid Sprint ID</div>;
  }

  return <SprintPageInternal sprintId={sprintId} />;
};

export default SprintPage;

interface State {
  mode: string;
  redirectTo?: string;
  sprint?: Sprint;
  sprintDates: Array<string>;
  sprintIssuesRows: Array<SprintIssue>;
  sprintSpilloverIssuesRows: Array<SprintIssue>;
  userAvailabilityRows: Array<SprintUser>;
  sprintPTOUsersRows: Array<SprintTimeOffUser>;
  issuesTableAssigneeFilterList: Array<{ id: number; login: string }>;
  issuesTableEpicFilterList: Array<{ text: string; value: string }>;
  userAvailabilityTableGroupFilterList: Array<{ text: string; value: string }>;
  userAvailabilityFilteredUsers: Array<SprintUser>;
  issuesVisible: boolean;
  spilloversVisible: boolean;
  usersVisible: boolean;
  ptoUsersVisible: boolean;
  backlogSprintId: number;
}

interface InternalProps {
  sprintId?: number;
}

export class SprintPageInternal extends React.PureComponent<
  InternalProps,
  State
> {
  constructor(props: InternalProps) {
    super(props);
    this.state = {
      mode: "sprint",
      redirectTo: undefined,
      sprint: undefined,
      sprintDates: [],
      sprintIssuesRows: [],
      sprintSpilloverIssuesRows: [],
      userAvailabilityRows: [],
      sprintPTOUsersRows: [],
      issuesTableAssigneeFilterList: [],
      issuesTableEpicFilterList: [],
      userAvailabilityTableGroupFilterList: [],
      userAvailabilityFilteredUsers: [],
      issuesVisible: true,
      spilloversVisible: true,
      usersVisible: true,
      ptoUsersVisible: true,
      backlogSprintId: 0,
    };
  }

  toggleMode = () => {
    const newMode = this.state.mode === "sprint" ? "backlog" : "sprint";
    this.setState({ mode: newMode }, () => {
      this.fetchSprintData();
    });
  };

  handleUserAvailabilityTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SprintUser> | SorterResult<SprintUser>[],
    extra: TableCurrentDataSource<SprintUser>,
  ) => {
    this.setState({
      userAvailabilityFilteredUsers: extra.currentDataSource,
    });
  };

  componentDidMount() {
    BitmakerAPIClient.get("/sprints/backlog", null, (response) => {
      const backlogSprint = response.data;
      this.setState({ backlogSprintId: backlogSprint.id }, () => {
        this.fetchSprintData();
      });
    });

    this.fetchIssuesTableAssigneeFilterListData();
  }

  fetchSprintData(mode = this.state.mode) {
    const sprintId =
      mode === "backlog" ? this.state.backlogSprintId : this.props.sprintId;

    const handleSprintResponse = (response: any) => {
      const sprint: Sprint = SprintFromJSON(response.data);
      const sprintDates = generateSprintDates(
        sprint.created_on,
        sprint.effective_date,
      );

      this.setState(
        {
          sprint: sprint,
          sprintDates: sprintDates,
        },
        () => {
          this.fetchSprintIssuesData(this.state.sprint?.id);
          this.fetchSprintSpilloverIssuesData(this.state.sprint?.id);
          this.fetchSprintPTOUsersData(this.state.sprint?.id);
          this.fetchUserAvailabilityData(this.state.sprint?.id);
        },
      );
    };

    if (sprintId) {
      BitmakerAPIClient.get(`/sprints/${sprintId}`, null, handleSprintResponse);
    } else {
      BitmakerAPIClient.get(`/sprints/current`, null, handleSprintResponse);
    }
  }

  fetchSprintIssuesData = (sprintID: number | undefined) => {
    const handleSprintIssuesResponse = (response: any) => {
      const sprintIssuesRows = (response.data as Array<SprintIssue>).map(
        SprintIssueFromJSON,
      );
      const issuesTableEpicFilterList = generateFilters(
        sprintIssuesRows,
        (issue) => issue.epicTags || [],
      );

      this.setState({
        sprintIssuesRows: sprintIssuesRows,
        issuesTableEpicFilterList: issuesTableEpicFilterList,
      });
    };

    BitmakerAPIClient.get(
      `/sprints/${sprintID}/issues`,
      {},
      handleSprintIssuesResponse,
    );
  };

  fetchIssuesTableAssigneeFilterListData = () => {
    const handleIssuesTableAssigneeFilterListResponse = (response: any) => {
      const raw = response?.data;
      this.setState({
        issuesTableAssigneeFilterList: Array.isArray(raw)
          ? raw.map((item) => UserBasicFromJSON(item as Record<string, any>))
          : [],
      });
    };

    BitmakerAPIClient.get(
      `/users/active`,
      null,
      handleIssuesTableAssigneeFilterListResponse,
    );
  };

  fetchSprintSpilloverIssuesData = (sprintID: number | undefined) => {
    const handleSprintSpilloverIssuesResponse = (response: any) => {
      this.setState({
        sprintSpilloverIssuesRows: (response.data as Array<SprintIssue>).map(
          SprintIssueFromJSON,
        ),
      });
    };

    BitmakerAPIClient.get(
      `/sprints/${sprintID}/issues/spillover`,
      {},
      handleSprintSpilloverIssuesResponse,
    );
  };

  fetchSprintPTOUsersData = (sprintID: number | undefined) => {
    const handlePTOUsersResponse = (response: any) => {
      this.setState({
        sprintPTOUsersRows: (response.data as Array<SprintTimeOffUser>).map(
          SprintTimeOffUserFromJSON,
        ),
      });
    };

    BitmakerAPIClient.get(
      `/sprints/${sprintID}/users/time-off`,
      {},
      handlePTOUsersResponse,
    );
  };

  fetchUserAvailabilityData = (sprintID: number | undefined) => {
    const handleUserAvailabilityResponse = (response: any) => {
      const userAvailabilityRows: Array<SprintUser> =
        response.data.map(SprintUserFromJSON);
      const userAvailabilityTableGroupFilterList = generateFilters(
        userAvailabilityRows,
        (userSprint) =>
          (userSprint.groups || []).map(
            (group: { name: string }) => group.name,
          ),
      );

      this.setState({
        userAvailabilityRows: userAvailabilityRows,
        userAvailabilityTableGroupFilterList:
          userAvailabilityTableGroupFilterList,
        userAvailabilityFilteredUsers: userAvailabilityRows,
      });
    };

    BitmakerAPIClient.get(
      `/sprints/${sprintID}/users`,
      null,
      handleUserAvailabilityResponse,
    );
  };

  issueColumns: ColumnsType<SprintIssue> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "5%",
      align: "center",
      render: (_, record) => (
        <a
          href={`${REDMINE_URL}/issues/${record.id}`}
          target="_blank"
          rel="noreferrer"
        >
          {record.id}
        </a>
      ),
    },
    {
      title: "Issue",
      dataIndex: "issue",
      key: "issue",
      width: "25%",
      render: (_, record) => (
        <a
          href={`${REDMINE_URL}/issues/${record.id}`}
          target="_blank"
          rel="noreferrer"
        >
          {record.subject}
        </a>
      ),
    },
    {
      title: "Assignee",
      dataIndex: "assigneeLogin",
      key: "assignee",
      width: "10%",
      align: "center",
      onFilter: (value, record) =>
        record.assigneeLogin ? record.assigneeLogin === value : false,
      render: (_, record) =>
        record.assigneeLogin ? (
          <a href={`/users/${record.assigneeLogin}`} rel="noreferrer">
            {record.assigneeLogin}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Account",
      dataIndex: "accountName",
      key: "account",
      width: "10%",
      align: "center",
      render: (_, record) =>
        record.accountName ? <p>{record.accountName}</p> : "-",
    },
    {
      title: "Epic",
      dataIndex: "epicSubject",
      key: "epic",
      width: "18%",
      onFilter: (value, record) =>
        (record.epicTags || []).some((tag) => tag === value),
      render: (_, record) => (
        <a
          href={`${REDMINE_URL}/issues/${record.epicId}`}
          target="_blank"
          rel="noreferrer"
        >
          {record.epicSubject}
        </a>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <span>
          {record.dueDate !== null
            ? fromDateToStringWithFullFormat(record.dueDate)
            : "-"}
        </span>
      ),
    },
    {
      title: "Estimated",
      dataIndex: "estimatedHours",
      key: "estimatedHours",
      width: "10%",
      align: "center",
      render: (_, record) => <p>{hoursReadable(record.estimatedHours)}</p>,
    },
    {
      title: "Spent",
      dataIndex: "spentHours",
      key: "spentHours",
      width: "10%",
      align: "center",
      render: (_, record) => <p>{hoursReadable(record.spentHours)}</p>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "12%",
      render: (_, record) => <p>{record.status}</p>,
    },
  ];

  userColumns: ColumnsType<SprintUser> = [
    {
      title: "User",
      dataIndex: "userDisplayName",
      key: "userDisplayName",
      width: "70%",
      onFilter: (value, record) =>
        record.groups.some((group) => group.name === value),
      render: (_, record) => (
        <a href={`/users/${record.userLogin}`} rel="noreferrer">
          {record.userDisplayName}
        </a>
      ),
    },
    {
      title: "Availability",
      dataIndex: "userSprintAvailability",
      key: "userSprintAvailability",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <p>{hoursReadable(record.userSprintAvailability)}</p>
      ),
    },
    {
      title: "Commitment",
      dataIndex: "userSprintCommitment",
      key: "userSprintCommitment",
      width: "10%",
      align: "center",
      render: (_, record) => {
        const c = record.userSprintCommitment;
        return (
          <p>
            {hoursReadable(
              c.estimated + c.fixed + c.epics + c.reviews + c.closed,
            )}
          </p>
        );
      },
    },
    {
      title: "Spent",
      dataIndex: "userSprintSpentTime",
      key: "userSprintSpentTime",
      width: "10%",
      align: "center",
      render: (_, record) => <p>{hoursReadable(record.userSprintSpentTime)}</p>,
    },
  ];

  ptoUsercolumns: ColumnsType<SprintTimeOffUser> = [
    {
      title: "User",
      dataIndex: "userDisplayName",
      key: "user",
      width: "50%",
      render: (_, record) => (
        <a href={`/users/${record.userLogin}`} rel="noreferrer">
          {record.userDisplayName}
        </a>
      ),
    },
  ];

  public render() {
    const {
      issuesTableEpicFilterList,
      userAvailabilityFilteredUsers,
      issuesTableAssigneeFilterList,
      issuesVisible,
      mode,
      ptoUsersVisible,
      redirectTo,
      spilloversVisible,
      sprint,
      sprintDates,
      sprintIssuesRows,
      sprintPTOUsersRows,
      sprintSpilloverIssuesRows,
      userAvailabilityRows,
      userAvailabilityTableGroupFilterList,
      usersVisible,
    } = this.state;

    const totalAvailability = userAvailabilityFilteredUsers.reduce(
      (total, user) => total + user.userSprintAvailability,
      0,
    );

    const totalCommitment = userAvailabilityFilteredUsers.reduce(
      (total, user) => {
        const c = user.userSprintCommitment;
        return total + c.estimated + c.fixed + c.epics + c.reviews + c.closed;
      },
      0,
    );

    const totalSpentTime = userAvailabilityFilteredUsers.reduce(
      (total, user) => total + user.userSprintSpentTime,
      0,
    );

    const totalUnassignedEstimatedHours = sprintIssuesRows
      .filter((issue) => !issue.assigneeLogin)
      .reduce((total, issue) => total + issue.estimatedHours, 0);

    if (redirectTo) {
      return <Navigate to={redirectTo} replace={true} />;
    }

    return (
      <div className="sprint-page">
        {sprint ? (
          <>
            <div className="sprint-header dark:bg-gray-700">
              <div
                className={classNames(
                  "sprint-header-nav",
                  "hover:shadow",
                  "hover:bg-gray-100",
                  "dark:hover:text-white",
                  "dark:hover:bg-gray-500",
                  "p-3",
                  "rounded-lg",
                )}
              >
                <a href={`/sprints/${sprint?.previous.id}`}>
                  <FontAwesomeIcon icon={faArrowAltCircleLeft} />
                  <span className={"sprint-nav-name"}>
                    {sprint.previous.name}
                  </span>
                </a>
              </div>
              <div className={classNames("sm:text-2xl", "sprint-name")}>
                {sprint.name}
              </div>
              <div
                className={classNames(
                  "sprint-header-nav",
                  "hover:shadow",
                  "hover:bg-gray-100",
                  "dark:hover:text-white",
                  "dark:hover:bg-gray-500",
                  "p-3",
                  "rounded-lg",
                )}
              >
                <a href={`/sprints/${sprint?.next.id}`}>
                  <span className={"sprint-nav-name"}>{sprint.next.name}</span>
                  <FontAwesomeIcon icon={faArrowAltCircleRight} />
                </a>
              </div>
            </div>

            {/* Spillovers table */}
            {sprintSpilloverIssuesRows.length > 0 ? (
              <div className="sprint-spillovers sprint-section">
                <div
                  className={classNames(
                    "sprint-section-title",
                    "cursor-pointer",
                    "dark:bg-slate-800",
                  )}
                  onClick={() =>
                    this.setState((prevState) => ({
                      spilloversVisible: !prevState.spilloversVisible,
                    }))
                  }
                >
                  <h1 className={"text-xl p-3"}> Spillovers </h1>
                  <Arrow direction={spilloversVisible ? "top" : "down"} />
                </div>
                <div
                  className={classNames(
                    { hidden: !spilloversVisible },
                    "sprint-section-listing",
                  )}
                >
                  <Table
                    columns={this.issueColumns}
                    dataSource={sprintSpilloverIssuesRows}
                    size={"large"}
                    pagination={{ pageSize: 10 }}
                    bordered
                    rowClassName={(record) =>
                      record.isClosed ? "issue-closed" : ""
                    }
                    scroll={{ x: true }}
                    rowKey={(record) => `spillover-${record.id}`}
                  />
                </div>
              </div>
            ) : null}

            <div className={classNames("sm:text-2xl", "button-center")}>
              <button onClick={this.toggleMode}>
                {mode === "sprint" ? "Ver Backlog" : "Ver Sprint"}
              </button>
            </div>

            {/* Issues table */}
            <div className="sprint-section">
              <div
                className={classNames(
                  "sprint-section-title",
                  "cursor-pointer",
                  "dark:bg-slate-800",
                )}
                onClick={() =>
                  this.setState((prevState) => ({
                    issuesVisible: !prevState.issuesVisible,
                  }))
                }
              >
                <h1 className={"text-xl p-3"}> Issues </h1>
                <Arrow direction={issuesVisible ? "top" : "down"} />
              </div>
              <div
                className={classNames(
                  { hidden: !issuesVisible },
                  "sprint-section-listing",
                )}
              >
                <Table
                  columns={this.issueColumns.map((column) => {
                    if (column.key === "epic") {
                      return {
                        ...column,
                        filters: issuesTableEpicFilterList,
                      };
                    } else if (column.key === "assignee") {
                      return {
                        ...column,
                        filters: issuesTableAssigneeFilterList.map(
                          (assignee) => ({
                            text: assignee.login,
                            value: assignee.login,
                          }),
                        ),
                      };
                    }
                    return column;
                  })}
                  dataSource={sprintIssuesRows}
                  size={"large"}
                  bordered
                  rowClassName={(record) => {
                    if (record.isClosed) {
                      return "issue-closed";
                    }
                    if (
                      record.dueDate === null ||
                      toUTCDateOnly(record.dueDate) <
                        new Date(new Date().toDateString())
                    ) {
                      if (
                        record.status !== "Recurring" &&
                        record.status !== "External review"
                      ) {
                        return "issue-due";
                      }
                    }
                    return "";
                  }}
                  scroll={{ x: true }}
                  rowKey={(record) => `issue-${record.id}`}
                />
              </div>
            </div>
          </>
        ) : null}

        {/* PTO Users table */}
        <div className={"sprint-section"}>
          <div
            className={classNames(
              "sprint-section-title",
              "cursor-pointer",
              "dark:bg-slate-800",
            )}
            onClick={() =>
              this.setState((prevState) => ({
                ptoUsersVisible: !prevState.ptoUsersVisible,
              }))
            }
          >
            <h1 className={"text-xl p-3"}> PTO Users </h1>
            <Arrow direction={ptoUsersVisible ? "top" : "down"} />
          </div>
        </div>
        <div
          className={classNames(
            { hidden: !ptoUsersVisible },
            "sprint-section-listing",
          )}
        >
          <Table
            columns={[
              ...this.ptoUsercolumns,
              ...sprintDates.map((date) => {
                const dateObj = new Date(date);
                const formattedColumnTitle = `${dateObj.toLocaleDateString(
                  "en-US",
                  {
                    weekday: "short",
                    timeZone: "UTC",
                  },
                )} ${dateObj.toLocaleDateString("en-GB", {
                  timeZone: "UTC",
                })}`;

                return {
                  title: formattedColumnTitle,
                  dataIndex: date,
                  key: date,
                  render: (_: any, record: SprintTimeOffUser) => (
                    <div style={{ textAlign: "center" }}>
                      {record.daysOff.includes(date) ? (
                        <MinusCircleFilled
                          style={{ color: "red", fontSize: "20px" }}
                        />
                      ) : null}
                    </div>
                  ),
                };
              }),
            ]}
            dataSource={sprintPTOUsersRows}
            size={"large"}
            pagination={false}
            bordered
            scroll={{ x: true }}
            rowKey={(record) => `pto-user-${record.userId}`}
          />
        </div>

        {/* User Availability table */}
        <div className={"sprint-section"}>
          <div
            className={classNames(
              "sprint-section-title",
              "cursor-pointer",
              "dark:bg-slate-800",
            )}
            onClick={() =>
              this.setState((prevState) => ({
                usersVisible: !prevState.usersVisible,
              }))
            }
          >
            <h1 className={"text-xl p-3"}> User Availability </h1>
            <Arrow direction={usersVisible ? "top" : "down"} />
          </div>
          <div
            className={classNames(
              { hidden: !usersVisible },
              "sprint-section-listing",
            )}
          >
            <Table
              columns={this.userColumns.map((column) => {
                if (column.key === "userDisplayName") {
                  return {
                    ...column,
                    filters: userAvailabilityTableGroupFilterList,
                  };
                }
                return column;
              })}
              dataSource={userAvailabilityRows}
              size={"large"}
              pagination={false}
              bordered
              scroll={{ x: true }}
              rowKey={(record) => record.userId}
              onChange={this.handleUserAvailabilityTableChange}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} className="summary-cell">
                    Total
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} className="summary-cell">
                    <p>{hoursReadable(totalAvailability)}</p>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} className="summary-cell">
                    <p>
                      {hoursReadable(
                        totalCommitment + totalUnassignedEstimatedHours,
                      )}{" "}
                      ({hoursReadable(totalCommitment)} +{" "}
                      {hoursReadable(totalUnassignedEstimatedHours)})
                    </p>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} className="summary-cell">
                    <p>{hoursReadable(totalSpentTime)}</p>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        </div>

        {sprint && userAvailabilityRows.length > 0 && (
          <div style={{ padding: "20px" }}>
            <h2 style={{ marginBottom: "10px" }}>Weekly availability</h2>
            <UserAvailabilityTrafficLight
              users={userAvailabilityRows}
              ptoUsers={sprintPTOUsersRows}
            />
          </div>
        )}
      </div>
    );
  }
}
